import { useQuery } from '@tanstack/react-query';
import { Sparkles, Users } from 'lucide-react';

import { cn } from '../lib/cn';

/**
 * Tiered founders promo. 99 zł always covers N months — the earlier
 * you sign up, the higher N. Each tier has a hard seat cap; once
 * filled, new signups land in the next tier with a smaller N.
 *
 *   Seats 1-10    → 99 zł / 12 months (the whole first year)
 *   Seats 11-100  → 99 zł / 6 months
 *   Seats 101-1000→ 99 zł / 3 months
 *   After 1000    → 99 zł / 1 month (standard)
 *
 * To adjust the ladder, edit TIERS below — UI derives everything from it.
 *
 * Counter pulls from api-worker `/api/public/signup-stats`. Endpoint
 * is still TBD (GO_LIVE step 5) — until it exists, the fetch fails
 * silently and we render a "be the first" state.
 */

export const PROMO_PRICE_PLN = 99;
export const REGULAR_YEAR_PRICE_PLN = 999;

export interface PromoTier {
  /** Inclusive seat number where this tier ends. Last tier is Infinity. */
  cap: number;
  /** How many months 99 zł covers for clinics landing in this tier. */
  months: number;
  /** Polish label used in copy. */
  label: string;
  /** Short pitch shown in cards. */
  pitch: string;
}

export const TIERS: PromoTier[] = [
  { cap: 10,       months: 12, label: 'Pakiet założycielski', pitch: 'cały rok za 99 zł' },
  { cap: 100,      months: 6,  label: 'Wczesny ptaszek',     pitch: '6 miesięcy za 99 zł' },
  { cap: 1000,     months: 3,  label: 'Promocja startowa',   pitch: '3 miesiące za 99 zł' },
  { cap: Infinity, months: 1,  label: 'Plan standardowy',    pitch: '99 zł / mies.' },
];

/** Index into TIERS for the offer active at userCount signups. */
export function tierIndexForCount(userCount: number): number {
  for (let i = 0; i < TIERS.length; i++) {
    if (userCount < TIERS[i]!.cap) return i;
  }
  return TIERS.length - 1;
}

/** Seats left in the active tier; 0 once the cap is hit. */
export function seatsLeftInCurrentTier(userCount: number): number {
  const t = TIERS[tierIndexForCount(userCount)]!;
  if (t.cap === Infinity) return 0;
  return Math.max(0, t.cap - userCount);
}

/** Total monthly value (zł) the clinic captures vs paying monthly. */
export function savingsZl(months: number): number {
  return Math.max(0, (months - 1) * PROMO_PRICE_PLN);
}

const API = import.meta.env.VITE_API_BASE_URL ?? '';

interface SignupStats {
  user_count: number;
  latest_signup_at: string | null; // ISO timestamp
  latest_signup_label?: string | null; // e.g. "Klinika Vet z Krakowa"
}

async function fetchStats(): Promise<SignupStats> {
  const res = await fetch(`${API}/api/public/signup-stats`, {
    headers: { accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`stats ${res.status}`);
  return (await res.json()) as SignupStats;
}

function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffSec = Math.max(0, (Date.now() - t) / 1000);
  if (diffSec < 60) return 'przed chwilą';
  if (diffSec < 60 * 60) return `${Math.round(diffSec / 60)} min temu`;
  if (diffSec < 60 * 60 * 24) return `${Math.round(diffSec / 3600)} godz. temu`;
  return `${Math.round(diffSec / 86400)} dni temu`;
}

function tierStart(idx: number): number {
  // Seats 1-10 belong to tier 0 (cap=10) → start = 0 (i.e. seat 1).
  return idx === 0 ? 0 : TIERS[idx - 1]!.cap;
}

export function PromoCounter({ variant = 'banner' }: { variant?: 'banner' | 'card' }) {
  const q = useQuery({
    queryKey: ['signup-stats'],
    queryFn: fetchStats,
    refetchInterval: 60_000, // pulse once a minute
    retry: 0,
  });

  const userCount = q.data?.user_count ?? 0;
  const idx = tierIndexForCount(userCount);
  const tier = TIERS[idx]!;
  const isStandard = tier.cap === Infinity;
  const seatsLeft = isStandard ? 0 : Math.max(0, tier.cap - userCount);
  const tierStartCount = tierStart(idx);
  const tierTotal = isStandard ? 0 : tier.cap - tierStartCount;
  const tierFilled = isStandard ? 0 : userCount - tierStartCount;
  const latestLabel = q.data?.latest_signup_label ?? null;
  const latestRel = fmtRelative(q.data?.latest_signup_at);

  if (variant === 'banner') {
    return (
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-accent-soft px-4 py-1.5 text-xs font-medium text-accent md:text-sm">
          <Sparkles className="size-3.5 shrink-0" aria-hidden="true" />
          <span>
            {isStandard ? (
              <>Promocja zakończona — plan standardowy 99 zł/mies.</>
            ) : (
              <>
                {tier.label}: <strong>{tier.pitch}</strong> · pozostało{' '}
                <strong>{seatsLeft}</strong> z {tierTotal} miejsc
              </>
            )}
          </span>
        </div>
        {!isStandard && latestLabel && (
          <p className="text-xs text-text-subtle">
            <Users className="mr-1 inline size-3" aria-hidden="true" />
            Ostatnio dołączyła: <span className="font-medium text-text">{latestLabel}</span>
            {latestRel && ` · ${latestRel}`}
          </p>
        )}
      </div>
    );
  }

  // 'card' variant — used on /cennik header. Shows the whole tier ladder.
  return (
    <div className="rounded-xl bg-surface p-5 ring-1 ring-border">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-text-subtle">
          Aktualny pakiet
        </span>
        <span className="font-display text-sm font-semibold text-accent tabular-nums">
          {userCount} klinik dołączyło
        </span>
      </div>

      <ul className="mt-4 space-y-3">
        {TIERS.filter((t) => t.cap !== Infinity).map((t, i) => {
          const start = tierStart(i);
          const total = t.cap - start;
          const filledHere = Math.max(0, Math.min(total, userCount - start));
          const isActive = i === idx;
          const isDone = userCount >= t.cap;
          return (
            <li key={t.label} className="text-sm">
              <div className="flex items-baseline justify-between gap-2">
                <span
                  className={cn(
                    'font-medium',
                    isActive ? 'text-text' : isDone ? 'text-text-subtle line-through' : 'text-text-muted',
                  )}
                >
                  {t.label} — {t.pitch}
                </span>
                <span
                  className={cn(
                    'tabular-nums text-xs',
                    isActive ? 'font-semibold text-accent' : 'text-text-subtle',
                  )}
                >
                  {filledHere}/{total}
                </span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-bg ring-1 ring-border">
                <div
                  className={cn(
                    'h-full transition-all',
                    isActive ? 'bg-accent' : isDone ? 'bg-text/30' : 'bg-border',
                  )}
                  style={{ width: `${(filledHere / total) * 100}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>

      <p className="mt-4 text-xs text-text-muted">
        {isStandard ? (
          <>Promocja zakończona. Plan standardowy: 99 zł/mies lub {REGULAR_YEAR_PRICE_PLN} zł/rok.</>
        ) : (
          <>
            <strong className="text-text">{tier.label}:</strong> 99 zł pokrywa{' '}
            {tier.months} {tier.months === 12 ? 'miesięcy (pełen rok)' : 'miesiące'}.
            {tier.months > 1 && (
              <> Oszczędzasz <strong className="text-accent">{savingsZl(tier.months)} zł</strong> w stosunku do planu miesięcznego.</>
            )}
          </>
        )}
      </p>

      {latestLabel && !isStandard && (
        <p className="mt-3 text-xs text-text-subtle">
          Ostatnio: <span className="font-medium text-text">{latestLabel}</span>
          {latestRel && ` · ${latestRel}`}
        </p>
      )}
    </div>
  );
}
